import { UUID } from "crypto";
import { Piece } from "../zodiacEngine/Piece";
import {
  Direction,
  GameState,
  IPiece,
  ISquare,
  Mode,
  PieceType,
  State,
} from "../zodiacEngine/types";
import { ZodiacEngine } from "../zodiacEngine/zodiacEngine";
import { FenToGameState } from "../zodiacEngine/Fen/FenLoader";
import { ZodiacFEN } from "../zodiacEngine/Fen";

// Status effect timing data - uses positions instead of IDs because IDs change when FEN is decoded
// This ensures Firebase acts as a dumb transport layer, preserving the engine's
// authoritative timing data instead of trying to reconstruct it
export interface StatusEffectData {
  poisoned?: Record<string, number>;    // "row,col" -> removeOnTurn (position-based, not ID)
  zombified?: Record<string, number>;   // "row,col" -> removeOnTurn (position-based, not ID)
  entangled?: string[];                 // positions as "row,col" (no timing needed)
  infected?: string[];                  // positions of infected pieces as "row,col" (virus stacking)
  shadowsActive?: boolean;              // If true, shadows exist and should be removed on next move
  shadowPositions?: string[];           // Positions of shadow pieces as "row,col" (FEN doesn't preserve isShadow)
  shadowedOctopusPosition?: string;     // Position of REAL octopus with isShadowed=true (the ink user)
  // Zombie ink shadow data - when a zombified octopus uses ink, owner must guess which is real
  isZombieInkShadow?: boolean;          // True if shadows were created by a zombified octopus
  zombieInkTargetColor?: boolean;       // The color (true=Light) that must deal with these shadows
}

// Simplified types for Firebase
export interface FirebaseGameState {
  moveHistory: string[];
  state: string; // Game state for reconnect queries (e.g., "Playing", "Piece Selection")
  lightKing?: string;
  darkKing?: string;
  lightPlayer?: string;
  darkPlayer?: string;
  lightPlayerUsername?: string;
  darkPlayerUsername?: string;
  isLightTurn: boolean;
  turnCount: number;
  lightWon: boolean | string; // nullable boolean
  lightElo?: number;
  darkElo?: number;
  timestamp?: number; // Date in YYYYMMDD format (e.g., 20251113)
  chatMessages?: string; // JSON string of chat messages array
  skipTurnChange?: boolean; // For bonus turns (synced to Firebase so both players see it)
  activeBonusTurnPieceId?: string; // For bonus turns (synced to Firebase so both players see it)
  // Time control fields
  timeControlPreset?: string; // 'sprint' | 'standard' | 'marathon' | 'off'
  lightTimeRemaining?: number; // milliseconds
  darkTimeRemaining?: number; // milliseconds
  lastMoveTimestamp?: number; // timestamp of last move (for latency compensation)
  // Status effect timing - engine is source of truth, Firebase just transports it
  statusEffects?: StatusEffectData;
  // Draw reason - needed to distinguish stalemate from repetition
  drawReason?: string;
  // Win condition - needed to distinguish timeout from kingslayer/race
  winCondition?: string;
  // Post-game king reveal - players can optionally show their king
  lightPlayerRevealedKing?: boolean;
  darkPlayerRevealedKing?: boolean;
  // Per-player matchmaking modes for cross-mode match handling (casual vs placement)
  lightPlayerMatchmakingMode?: 'competitive' | 'casual' | 'placement';
  darkPlayerMatchmakingMode?: 'competitive' | 'casual' | 'placement';
  // Legacy matchmakingMode field (used as fallback when per-player modes aren't available)
  matchmakingMode?: 'competitive' | 'casual';
  // Player presence timestamps for disconnect detection
  lightPlayerLastSeen?: number;
  darkPlayerLastSeen?: number;
  // Player tab visibility for disconnect detection
  lightPlayerTabHidden?: boolean;
  darkPlayerTabHidden?: boolean;
  // Bot game fields
  isBotGame?: boolean;
  botPlayerId?: string;
  botAiModel?: string;
  botTimeFrame?: string;
}

/**
 * Extract status effect timing data from the board
 * This captures the engine's authoritative removeOnTurn values so Firebase can transport them
 */
function extractStatusEffects(board: ISquare[][]): StatusEffectData | undefined {

  // Use positions instead of IDs because piece IDs change when FEN is decoded
  const poisoned: Record<string, number> = {};
  const zombified: Record<string, number> = {};
  const entangled: string[] = [];
  const infected: string[] = [];

  for (let row = 0; row < board.length; row++) {
    for (let col = 0; col < board[row].length; col++) {
      const square = board[row][col];
      if (!square.Pieces) continue;
      const posKey = `${row},${col}`;

      for (const piece of square.Pieces) {
        // Extract poison timing (position-based)
        if (piece.poisonedStatus?.isPoisoned && piece.poisonedStatus.removeOnTurn !== undefined) {
          poisoned[posKey] = piece.poisonedStatus.removeOnTurn;
        }

        // Extract zombification timing (position-based)
        if (piece.zombifiedStatus?.isZombified && piece.zombifiedStatus.removeOnTurn !== undefined) {
          zombified[posKey] = piece.zombifiedStatus.removeOnTurn;
        } else if (piece.zombifiedStatus?.isZombified) {
        }

        // Extract entanglement (position-based, no timing needed)
        if (piece.isEntangled) {
          entangled.push(posKey);
        }

        // Extract infection (position-based, for virus stacking)
        // This ensures the virus moves with the infected piece
        if (piece.isInfected) {
          infected.push(posKey);
        }
      }
    }
  }

  // Check if any shadow pieces exist and collect their positions
  // FEN doesn't preserve isShadow flag, so we need to track them separately
  const shadowPositions: string[] = [];
  let shadowedOctopusPosition: string | undefined;
  let isZombieInkShadow: boolean | undefined;
  let zombieInkTargetColor: boolean | undefined;
  for (let row = 0; row < board.length; row++) {
    for (let col = 0; col < board[row].length; col++) {
      const square = board[row][col];
      if (!square.Pieces) continue;
      for (const piece of square.Pieces) {
        if (piece.isShadow) {
          shadowPositions.push(`${row},${col}`);
          // Track zombie ink shadow data - if any shadow has these properties, capture them
          if (piece.isZombieInkShadow) {
            isZombieInkShadow = true;
            zombieInkTargetColor = piece.zombieInkTargetColor;
          }
        }
        // Also track the REAL octopus that used ink (has isShadowed = true)
        // This is critical for multiplayer - without it the opponent can identify the king
        // because the king octopus won't have the shadowed visual effect
        if (piece.type === PieceType.Octopus && piece.isShadowed && !piece.isShadow) {
          shadowedOctopusPosition = `${row},${col}`;
        }
      }
    }
  }
  const shadowsActive = shadowPositions.length > 0;

  // Only return if there are any status effects
  const hasEffects = Object.keys(poisoned).length > 0 ||
                     Object.keys(zombified).length > 0 ||
                     entangled.length > 0 ||
                     infected.length > 0 ||
                     shadowsActive ||
                     shadowedOctopusPosition !== undefined;


  if (!hasEffects) return undefined;

  const result: StatusEffectData = {};
  if (Object.keys(poisoned).length > 0) result.poisoned = poisoned;
  if (Object.keys(zombified).length > 0) result.zombified = zombified;
  if (entangled.length > 0) result.entangled = entangled;
  if (infected.length > 0) result.infected = infected;
  if (shadowsActive) {
    result.shadowsActive = true;
    result.shadowPositions = shadowPositions;
    // Include zombie ink data if shadows were created by a zombified octopus
    if (isZombieInkShadow) {
      result.isZombieInkShadow = true;
      result.zombieInkTargetColor = zombieInkTargetColor;
    }
  }
  if (shadowedOctopusPosition) {
    result.shadowedOctopusPosition = shadowedOctopusPosition;
  }

  return result;
}

export function convertToFirebaseState(
  gameState: GameState,
  moveMade: boolean = false,
  lightElo?: number,
  darkElo?: number,
  timeControlData?: {
    preset?: string;
    lightTimeRemaining?: number;
    darkTimeRemaining?: number;
    lastMoveTimestamp?: number;
  }
): FirebaseGameState {

  // Get full move history
  // Use encodeWithoutKings to match the engine's FEN encoding (no king markers, no d prefix)
  // This prevents phantom entries where consecutive FENs differ only in king markers
  const fullHistory = moveMade
    ? gameState.fenHistory.concat(ZodiacFEN.encodeWithoutKings(gameState))
    : gameState.fenHistory;

  // Normalize encoding for backward compatibility with old in-flight FENs
  // After harmonizing encodeSpecialAbilitiesWithoutKing(), new FENs use ✳️ consistently,
  // but old FENs in history may still have ASCII * markers
  const moveHistory = fullHistory.map(fen => fen.replace(/\*/g, '✳️'));

  // Determine lightWon value - use "draw" for draws, boolean for wins, empty for ongoing
  let lightWonValue: boolean | string = "";
  if (gameState.state === State.Draw) {
    lightWonValue = "draw";
  } else if (gameState.lightWon !== undefined) {
    lightWonValue = gameState.lightWon;
  }

  const baseState = {
    moveHistory: moveHistory,
    state: gameState.state, // CRITICAL: Include state for reconnect queries
    lightKing: gameState.lightKing ?? "",
    darkKing: gameState.darkKing ?? "",
    lightPlayer: gameState.lightPlayer ?? "",
    darkPlayer: gameState.darkPlayer ?? "",
    lightPlayerUsername: gameState.lightPlayerUsername ?? "",
    darkPlayerUsername: gameState.darkPlayerUsername ?? "",
    isLightTurn: gameState.isLightTurn,
    turnCount: gameState.turnCount,
    lightWon: lightWonValue,
  };

  // Add optional fields only if they're defined (Firebase doesn't allow undefined)
  const optionalFields: Partial<FirebaseGameState> = {};

  // Add ELO ratings if provided
  if (lightElo !== undefined) {
    optionalFields.lightElo = lightElo;
  }
  if (darkElo !== undefined) {
    optionalFields.darkElo = darkElo;
  }

  // Add timestamp in YYYYMMDD format for analytics
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  optionalFields.timestamp = parseInt(`${year}${month}${day}`);

  // CRITICAL: Add bonus turn state to Firebase sync
  // This ensures both players know when a bonus turn is active (monkey swing, virus infection, penguin rotation)
  // Without this, the opponent sees the turn change immediately and can move
  if (gameState.skipTurnChange !== undefined) {
    optionalFields.skipTurnChange = gameState.skipTurnChange;
  }
  if (gameState.activeBonusTurnPieceId !== undefined && gameState.activeBonusTurnPieceId !== null) {
    optionalFields.activeBonusTurnPieceId = gameState.activeBonusTurnPieceId;
  }

  // Add time control data if provided
  if (timeControlData?.preset !== undefined) {
    optionalFields.timeControlPreset = timeControlData.preset;
  }
  if (timeControlData?.lightTimeRemaining !== undefined) {
    optionalFields.lightTimeRemaining = timeControlData.lightTimeRemaining;
  }
  if (timeControlData?.darkTimeRemaining !== undefined) {
    optionalFields.darkTimeRemaining = timeControlData.darkTimeRemaining;
  }
  if (timeControlData?.lastMoveTimestamp !== undefined) {
    optionalFields.lastMoveTimestamp = timeControlData.lastMoveTimestamp;
  }

  // Extract status effect timing data from the board
  // Engine is the source of truth - Firebase just transports these values
  const statusEffects = extractStatusEffects(gameState.board);
  if (statusEffects) {
    optionalFields.statusEffects = statusEffects;
  }

  // Sync draw reason for distinguishing stalemate from repetition
  // Check both drawReason and repeatString (legacy field)
  const drawReason = (gameState as any).drawReason;
  const repeatString = (gameState as any).repeatString;
  if (drawReason) {
    optionalFields.drawReason = drawReason;
  } else if (repeatString && repeatString.includes("3x")) {
    optionalFields.drawReason = "repetition";
  }

  // Sync win condition for distinguishing timeout from kingslayer/race
  const winCondition = (gameState as any).winCondition;
  if (winCondition) {
    optionalFields.winCondition = winCondition;
  }

  // Sync per-player matchmaking modes for cross-mode match handling
  if (gameState.lightPlayerMatchmakingMode) {
    optionalFields.lightPlayerMatchmakingMode = gameState.lightPlayerMatchmakingMode;
  }
  if (gameState.darkPlayerMatchmakingMode) {
    optionalFields.darkPlayerMatchmakingMode = gameState.darkPlayerMatchmakingMode;
  }
  // Sync legacy matchmakingMode (used as fallback when per-player modes aren't available)
  if (gameState.matchmakingMode) {
    optionalFields.matchmakingMode = gameState.matchmakingMode;
  }

  // Sync player presence timestamps for disconnect detection
  if (gameState.lightPlayerLastSeen) {
    optionalFields.lightPlayerLastSeen = gameState.lightPlayerLastSeen;
  }
  if (gameState.darkPlayerLastSeen) {
    optionalFields.darkPlayerLastSeen = gameState.darkPlayerLastSeen;
  }
  // Sync player tab visibility for disconnect detection
  if (gameState.lightPlayerTabHidden !== undefined) {
    optionalFields.lightPlayerTabHidden = gameState.lightPlayerTabHidden;
  }
  if (gameState.darkPlayerTabHidden !== undefined) {
    optionalFields.darkPlayerTabHidden = gameState.darkPlayerTabHidden;
  }

  // Bot game fields
  if (gameState.isBotGame) {
    optionalFields.isBotGame = true;
    if (gameState.botPlayerId) optionalFields.botPlayerId = gameState.botPlayerId;
    if (gameState.botAiModel) optionalFields.botAiModel = gameState.botAiModel;
    if (gameState.botTimeFrame) optionalFields.botTimeFrame = gameState.botTimeFrame;
  }

  const result = {
    ...baseState,
    ...optionalFields,
  };

  return result;
}

export function convertFromFirebaseState(
  firebaseState: FirebaseGameState
): GameState {
  // take the item at the end of the move history and convert it to a board state
  var convertedBoardState = firebaseState.moveHistory.slice(-1)[0];
  if (!convertedBoardState) {
    convertedBoardState = "8/8/8/8/8";
  }


  const newGameState = FenToGameState(convertedBoardState, new ZodiacEngine());

  // Apply status effect timing from Firebase (engine is source of truth)
  // This replaces the old guessing logic with authoritative data
  applyStatusEffects(newGameState.board, firebaseState.statusEffects, firebaseState.turnCount);

  // CRITICAL FIX: Validate bonus turn state consistency
  // If skipTurnChange is false (bonus turn ended), activeBonusTurnPieceId MUST be cleared
  // This prevents stale piece IDs from blocking piece selection after bonus turns end
  let validatedSkipTurnChange = firebaseState.skipTurnChange;

  // Convert empty string (used to clear field in Firebase) back to undefined
  let validatedActiveBonusTurnPieceId =
    firebaseState.activeBonusTurnPieceId &&
    firebaseState.activeBonusTurnPieceId !== ""
      ? firebaseState.activeBonusTurnPieceId
      : undefined;

  // Clear invalid bonus turn state (bonus turn ended but piece ID still set)
  if (validatedSkipTurnChange === false && validatedActiveBonusTurnPieceId) {
    validatedActiveBonusTurnPieceId = undefined;
  }

  // Determine game state - prefer explicit state from Firebase, fall back to inference for old games
  let inferredState: State;

  // Valid State enum values that can be stored in Firebase
  const validStates = Object.values(State) as string[];

  // Use explicit state from Firebase if available and valid
  if (firebaseState.state && validStates.includes(firebaseState.state)) {
    inferredState = firebaseState.state as State;
  } else if (firebaseState.lightWon === "draw") {
    // Game ended in a draw (stalemate, repetition, etc.)
    inferredState = State.Draw;
  } else if (firebaseState.lightWon !== undefined && firebaseState.lightWon !== "") {
    // Game has ended with a winner
    inferredState = State.PostGame;
  } else if (!firebaseState.lightPlayer || !firebaseState.darkPlayer) {
    // Missing a player - still searching
    inferredState = State.Searching;
  } else if (!firebaseState.lightKing || !firebaseState.darkKing) {
    // Both players present but kings not selected
    inferredState = State.KingSelection;
  } else {
    // FALLBACK for old games without explicit state field:
    // Check if board has all 10 pieces to determine if piece selection is complete
    // This is more semantic than counting moveHistory length, and works correctly
    // even after the FEN history is cleaned when the game starts
    const lastFen = firebaseState.moveHistory.slice(-1)[0] || '';
    // Strip turn indicator prefix ('d') and count piece characters
    const cleanFen = lastFen.startsWith('d') ? lastFen.slice(1) : lastFen;
    const pieceCount = (cleanFen.match(/[A-Za-z]/g) || []).length;

    if (pieceCount < 10) {
      // Board doesn't have all 10 pieces yet - still in piece selection
      inferredState = State.PieceSelection;
    } else {
      // Full board (10 pieces) - game is in progress
      inferredState = State.Playing;
    }
  }

  return {
    mode: Mode.Live, // All multiplayer games are Live mode
    fenHistory: firebaseState.moveHistory,
    state: inferredState,
    board: newGameState.board,
    lightKing: (firebaseState.lightKing as PieceType) || undefined,
    darkKing: (firebaseState.darkKing as PieceType) || undefined,
    lightPlayer: firebaseState.lightPlayer || undefined,
    darkPlayer: firebaseState.darkPlayer || undefined,
    lightPlayerUsername: firebaseState.lightPlayerUsername || undefined,
    darkPlayerUsername: firebaseState.darkPlayerUsername || undefined,
    isLightTurn: firebaseState.isLightTurn as boolean,
    turnCount: firebaseState.turnCount as number,
    // CRITICAL: Must check for boolean type, not truthiness!
    // When Dark wins, lightWon is FALSE (not undefined). Using ternary ? treats false as falsy.
    lightWon: typeof firebaseState.lightWon === "boolean"
      ? firebaseState.lightWon
      : undefined,
    skipTurnChange: validatedSkipTurnChange,
    activeBonusTurnPieceId: validatedActiveBonusTurnPieceId,
    // Pass shadow state so engine can set shadowSet flag for proper removal on next move
    shadowsActive: firebaseState.statusEffects?.shadowsActive,
    // Restore draw reason for distinguishing stalemate from repetition
    drawReason: firebaseState.drawReason,
    // Restore win condition for distinguishing timeout from kingslayer/race
    winCondition: firebaseState.winCondition,
    // CRITICAL: Pass time control preset for MMR per-time-control ratings
    timeControlPreset: firebaseState.timeControlPreset,
    // Restore per-player matchmaking modes for cross-mode match handling
    lightPlayerMatchmakingMode: firebaseState.lightPlayerMatchmakingMode,
    darkPlayerMatchmakingMode: firebaseState.darkPlayerMatchmakingMode,
    // Restore legacy matchmakingMode (used as fallback when per-player modes aren't available)
    matchmakingMode: firebaseState.matchmakingMode,
    // Restore player presence timestamps for disconnect detection
    lightPlayerLastSeen: firebaseState.lightPlayerLastSeen,
    darkPlayerLastSeen: firebaseState.darkPlayerLastSeen,
    // Restore player tab visibility for disconnect detection
    lightPlayerTabHidden: firebaseState.lightPlayerTabHidden,
    darkPlayerTabHidden: firebaseState.darkPlayerTabHidden,
    // Restore bot game fields
    isBotGame: firebaseState.isBotGame,
    botPlayerId: firebaseState.botPlayerId,
    botAiModel: firebaseState.botAiModel,
    botTimeFrame: firebaseState.botTimeFrame,
  } as GameState;
}

/**
 * Apply status effect timing data from Firebase to pieces on the board
 *
 * This is the proper solution: Firebase transports the engine's authoritative timing data.
 * No more guessing with magic constants - the engine sets removeOnTurn when the effect
 * is applied, and Firebase just passes it along.
 *
 * For backward compatibility with old games that don't have statusEffects data,
 * we fall back to a simple heuristic (but this should rarely be needed).
 */
function applyStatusEffects(
  board: ISquare[][],
  statusEffects: StatusEffectData | undefined,
  turnCount: number
): void {

  // Apply authoritative timing data from Firebase (if available)
  // Uses position-based lookup because piece IDs change when FEN is decoded
  if (statusEffects) {
    // Apply poison timing (position-based)
    if (statusEffects.poisoned) {
      for (const [posKey, removeOnTurn] of Object.entries(statusEffects.poisoned)) {
        const [row, col] = posKey.split(',').map(Number);
        const square = board[row]?.[col];
        if (square?.Pieces) {
          for (const piece of square.Pieces) {
            if (piece.poisonedStatus?.isPoisoned) {
              piece.poisonedStatus.removeOnTurn = removeOnTurn;
            }
          }
        }
      }
    }

    // Apply zombification timing (position-based)
    if (statusEffects.zombified) {
      for (const [posKey, removeOnTurn] of Object.entries(statusEffects.zombified)) {
        const [row, col] = posKey.split(',').map(Number);
        const square = board[row]?.[col];
        if (square?.Pieces) {
          for (const piece of square.Pieces) {
            if (piece.zombifiedStatus?.isZombified) {
              piece.zombifiedStatus.removeOnTurn = removeOnTurn;
            }
          }
        }
      }
    } else {
    }

    // Entanglement doesn't need timing - it clears on the piece's next turn
    // The FEN already marks pieces as entangled, so nothing to do here

    // Restore infection status - critical for virus movement with host
    // When an infected piece moves, the virus should move with it
    if (statusEffects.infected) {
      for (const posStr of statusEffects.infected) {
        const [row, col] = posStr.split(',').map(Number);
        const square = board[row]?.[col];
        if (square?.Pieces) {
          for (const piece of square.Pieces) {
            // Mark non-Virus pieces at infected positions as infected
            if (piece.type !== PieceType.Virus) {
              piece.isInfected = true;
            }
          }
        }
      }
    }

    // Mark shadow pieces - FEN doesn't preserve isShadow flag
    // We stored shadow positions when syncing to Firebase
    if (statusEffects.shadowPositions) {
      for (const posStr of statusEffects.shadowPositions) {
        const [row, col] = posStr.split(',').map(Number);
        const square = board[row]?.[col];
        if (square?.Pieces) {
          for (const piece of square.Pieces) {
            // Mark octopi at shadow positions as shadows
            if (piece.type === PieceType.Octopus) {
              piece.isShadow = true;
              // Restore zombie ink shadow data if present
              if (statusEffects.isZombieInkShadow) {
                piece.isZombieInkShadow = true;
                piece.zombieInkTargetColor = statusEffects.zombieInkTargetColor;
              }
            }
          }
        }
      }
    }

    // Restore isShadowed on the REAL octopus that used ink
    // This is critical for multiplayer - without it the opponent can identify the king
    // because the king octopus appears at full opacity while shadows are transparent
    if (statusEffects.shadowedOctopusPosition) {
      const [row, col] = statusEffects.shadowedOctopusPosition.split(',').map(Number);
      const square = board[row]?.[col];
      if (square?.Pieces) {
        for (const piece of square.Pieces) {
          if (piece.type === PieceType.Octopus && !piece.isShadow) {
            piece.isShadowed = true;
          }
        }
      }
    }
  }

  // BACKWARD COMPATIBILITY: For old games without statusEffects data,
  // apply a fallback heuristic for any pieces that still need timing.
  // This uses the engine's Effect.Poison() duration (4 turns).
  //
  // CRITICAL FIX: If statusEffects IS defined but doesn't contain an entry for
  // a zombified/poisoned piece, it means the effect was CLEARED on the source
  // engine. In that case, we should clear it here too instead of re-applying.
  // This fixes the multiplayer zombification timer bug where zombification would
  // persist indefinitely because the FEN history contains zombie markers even
  // after the effect was cleared.
  const POISON_DURATION = 4;  // Matches Effect.Poison() in types.ts
  const ZOMBIE_DURATION = 2;  // Matches Effect.Zombify() in types.ts
  const hasStatusEffectsData = statusEffects !== undefined;

  for (let row = 0; row < board.length; row++) {
    for (let col = 0; col < board[row].length; col++) {
      const square = board[row][col];
      if (!square.Pieces) continue;
      const posKey = `${row},${col}`;

      for (const piece of square.Pieces) {
        // Handle poison without timing
        if (piece.poisonedStatus?.isPoisoned && piece.poisonedStatus.removeOnTurn === undefined) {
          if (hasStatusEffectsData && !statusEffects.poisoned?.[posKey]) {
            // statusEffects exists but no entry for this piece = intentionally cleared
            // Clear the poison that was incorrectly set from FEN
            piece.poisonedStatus = {
              isPoisoned: false,
              removeOnTurn: undefined
            };
          } else if (!hasStatusEffectsData) {
            // Old game without statusEffects - use fallback heuristic
            piece.poisonedStatus.removeOnTurn = turnCount + POISON_DURATION;
          }
        }

        // Handle zombification without timing
        if (piece.zombifiedStatus?.isZombified && piece.zombifiedStatus.removeOnTurn === undefined) {
          if (hasStatusEffectsData && !statusEffects?.zombified?.[posKey]) {
            // statusEffects exists but no entry for this piece = intentionally cleared
            // Clear the zombification that was incorrectly set from FEN
            piece.zombifiedStatus = {
              isZombified: false,
              removeOnTurn: undefined
            };
            piece.isZombified = false;
          } else if (!hasStatusEffectsData) {
            // Old game without statusEffects - use fallback heuristic
            piece.zombifiedStatus.removeOnTurn = turnCount + ZOMBIE_DURATION;
          }
        } else if (piece.zombifiedStatus?.isZombified) {
        }
      }
    }
  }

  // CRITICAL FIX: Clear any status effects that are already expired
  // This handles the case where Firebase sends state with effects that should have expired
  // (because sendUpdateToRemote runs BEFORE clearTimedStatusEffects on the sender's engine)
  for (let row = 0; row < board.length; row++) {
    for (let col = 0; col < board[row].length; col++) {
      const square = board[row][col];
      if (!square.Pieces) continue;
      const posKey = `${row},${col}`;

      for (const piece of square.Pieces) {
        // Clear expired poison
        if (
          piece.poisonedStatus?.isPoisoned &&
          piece.poisonedStatus.removeOnTurn !== undefined &&
          piece.poisonedStatus.removeOnTurn <= turnCount
        ) {
          piece.poisonedStatus = {
            isPoisoned: false,
            removeOnTurn: undefined
          };
        }

        // Clear expired zombification (use < not <= so Dark gets to move the zombified piece)
        if (
          piece.zombifiedStatus?.isZombified &&
          piece.zombifiedStatus.removeOnTurn !== undefined &&
          piece.zombifiedStatus.removeOnTurn < turnCount
        ) {
          piece.zombifiedStatus = {
            isZombified: false,
            removeOnTurn: undefined
          };
          piece.isZombified = false;
        }
      }
    }
  }
}
